<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAlert } from 'dashboard/composables'
import API from 'dashboard/api/crm/activities'
import TaskCard from './TaskCard.vue'
import TaskFormModal from './TaskFormModal.vue'

const props = defineProps({
  contactId:      { type: Number, default: null },
  conversationId: { type: Number, default: null },
})

const { t } = useI18n()
const showModal = ref(false)
const selectedActivity = ref(null)
const localActivities = ref([])
const isFetching = ref(false)

async function fetchActivities() {
  isFetching.value = true
  try {
    const params = {}
    if (props.contactId)      params.contact_id      = props.contactId
    if (props.conversationId) params.conversation_id = props.conversationId
    const { data } = await API.get(params)
    localActivities.value = Array.isArray(data) ? data : []
  } catch {
    // swallow: widget shows empty state
  } finally {
    isFetching.value = false
  }
}

onMounted(fetchActivities)
watch(() => [props.contactId, props.conversationId], fetchActivities)

const pendingActivities = computed(() =>
  localActivities.value
    .filter(a => a.status !== 'done' && a.status !== 'cancelled')
    .sort((a, b) => new Date(a.due_at) - new Date(b.due_at))
    .slice(0, 5)
)

function openCreate() {
  selectedActivity.value = null
  showModal.value = true
}

async function handleComplete(activity) {
  try {
    await API.update(activity.id, { status: 'done' })
    await fetchActivities()
    useAlert(t('TASKS.ALERTS.COMPLETE_SUCCESS'))
  } catch {
    useAlert(t('TASKS.ALERTS.COMPLETE_ERROR'))
  }
}

async function handleDelete(activity) {
  if (!window.confirm(t('TASKS.WIDGET.CONFIRM_DELETE', { subject: activity.subject }))) return
  try {
    await API.delete(activity.id)
    await fetchActivities()
    useAlert(t('TASKS.ALERTS.DELETE_SUCCESS'))
  } catch {
    useAlert(t('TASKS.ALERTS.DELETE_ERROR'))
  }
}

async function handleSaved() {
  await fetchActivities()
  selectedActivity.value = null
}
</script>

<template>
  <div class="tasks-widget">
    <div class="flex items-center justify-between mb-3">
      <button
        class="flex items-center gap-1.5 text-xs text-n-brand hover:brightness-110 border-0 outline-none bg-transparent cursor-pointer transition-all"
        @click="openCreate"
      >
        <fluent-icon icon="add" size="12" />
        {{ $t('TASKS.WIDGET.ADD_BUTTON') }}
      </button>
    </div>

    <div v-if="isFetching" class="text-xs text-n-slate-11 py-2">
      {{ $t('TASKS.WIDGET.LOADING') }}
    </div>

    <div v-else-if="pendingActivities.length === 0" class="flex flex-col items-center py-4 text-n-slate-11">
      <fluent-icon icon="clipboard" size="24" class="mb-2 text-n-slate-9" />
      <p class="text-xs">{{ $t('TASKS.WIDGET.EMPTY_STATE') }}</p>
    </div>

    <div v-else class="space-y-2">
      <TaskCard
        v-for="activity in pendingActivities"
        :key="activity.id"
        :activity="activity"
        @edit="a => { selectedActivity = a; showModal = true }"
        @complete="handleComplete"
        @delete="handleDelete"
      />
    </div>

    <TaskFormModal
      v-model="showModal"
      :activity="selectedActivity"
      :prefill-contact-id="contactId"
      :prefill-conversation-id="conversationId"
      lock-associations
      @saved="handleSaved"
    />
  </div>
</template>
